import * as React from "react";
import { db } from "../../../firebase-info.js";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { NeighbourContext, DataContext } from "./contexts.js";
import { BATCH_SIZE } from "../../utils/constants.jsx";

const DataContextComponent = ({ children }) => {
  const neighbourContextValue = React.useContext(NeighbourContext);

  const [currentCommunityId, setCurrentCommunityId] = React.useState("");

  const [announcesData, setAnnouncesData] = React.useState([]);
  const [meetingsData, setMeetingsData] = React.useState([]);
  const [deliveriesData, setDeliveriesData] = React.useState([]);
  const [ticketsData, setTicketsData] = React.useState([]);

  const [allAnnouncesLoaded, setAllAnnouncesLoaded] = React.useState(false);
  const [allMeetingsLoaded, setAllMeetingsLoaded] = React.useState(false);
  const [allDeliveriesLoaded, setAllDeliveriesLoaded] = React.useState(false);
  const [allTicketsLoaded, setAllTicketsLoaded] = React.useState(false);

  const [lvAnnounce, setLastVisibleAnnounce] = React.useState(null);
  const [lvMeeting, setLastVisibleMeeting] = React.useState(null);
  const [lvDelivery, setLastVisibleDelivery] = React.useState(null);
  const [lvTicket, setLastVisibleTicket] = React.useState(null);

  const [loadingAnnounces, setLoadingAnnounces] = React.useState(false);
  const [loadingMeetings, setLoadingMeetings] = React.useState(false);
  const [loadingDeliveries, setLoadingDeliveries] = React.useState(false);
  const [loadingTickets, setLoadingTickets] = React.useState(false);

  const [loading, setLoading] = React.useState(true);

  const dataMap = {
    announces: {
      data: announcesData,
      lastVisible: lvAnnounce,
      setData: setAnnouncesData,
      setLastVisible: setLastVisibleAnnounce,
      setAllLoaded: setAllAnnouncesLoaded,
      unsubscribe: null,
      setLoading: setLoadingAnnounces,
    },
    meetings: {
      data: meetingsData,
      lastVisible: lvMeeting,
      setData: setMeetingsData,
      setLastVisible: setLastVisibleMeeting,
      setAllLoaded: setAllMeetingsLoaded,
      unsubscribe: null,
      setLoading: setLoadingMeetings,
    },
    deliveries: {
      data: deliveriesData,
      lastVisible: lvDelivery,
      setData: setDeliveriesData,
      setLastVisible: setLastVisibleDelivery,
      setAllLoaded: setAllDeliveriesLoaded,
      unsubscribe: null,
      setLoading: setLoadingDeliveries,
    },
    tickets: {
      data: ticketsData,
      lastVisible: lvTicket,
      setData: setTicketsData,
      setLastVisible: setLastVisibleTicket,
      setAllLoaded: setAllTicketsLoaded,
      unsubscribe: null,
      setLoading: setLoadingTickets,
    },
  };

  const unsetAllLoaded = (coll) => {
    if (!Object.keys(dataMap).includes(coll)) {
      return;
    }

    const data = dataMap[coll];

    data.setAllLoaded(false);
  };

  const loadMore = (
    coll,
    howMany = BATCH_SIZE,
    extraConditions = [],
    force = false
  ) => {
    if (!Object.keys(dataMap).includes(coll)) {
      return;
    }

    const commRef = neighbourContextValue.neighbourData?.communityRef;
    if (!commRef) {
      return;
    }

    let qry = query(
      collection(db, coll),
      where("communityRef", "==", commRef),
      where("published", "==", true),
      ...extraConditions,
      orderBy("publishingDate", "desc"),
      limit(howMany)
    );
    const data = dataMap[coll];

    data.setLoading(true);

    if (data.lastVisible && !force) {
      qry = query(
        collection(db, coll),
        where("communityRef", "==", commRef),
        where("published", "==", true),
        ...extraConditions,
        orderBy("publishingDate", "desc"),
        startAfter(data.lastVisible),
        limit(howMany)
      );
    }

    if (force) {
      data.setAllLoaded(false);
    }

    data.unsubscribe = onSnapshot(qry, async (querySnapshot) => {
      const count = querySnapshot.docs.length;
      if (count) {
        const newData = querySnapshot.docs.map((e) => ({
          ...e.data(),
          ref: e.ref,
        }));
        data.setLastVisible(querySnapshot.docs[count - 1]);

        if (count < howMany) {
          data.setAllLoaded(true);
        }

        if (force) {
          const nuevosDatos = [...newData];
          data.setData(nuevosDatos);
        } else {
          const nuevosDatos = [...data.data, ...newData];
          data.setData(nuevosDatos);
        }
      } else {
        data.setAllLoaded(true);
      }

      data.setLoading(false);
    });
  };

  const clearContext = () => {
    Object.keys(dataMap).forEach((d) => {
      const elem = dataMap[d];

      if (elem.unsubscribe) {
        elem.unsubscribe();
      }

      elem.setLastVisible(null);
      elem.setAllLoaded(false);
      elem.data.length = 0;
      elem.setData([]);
    });
  };

  React.useEffect(() => {
    if (!neighbourContextValue.loading) {
      if (neighbourContextValue.communityData) {
        if (currentCommunityId !== neighbourContextValue.communityData?.id) {
          setCurrentCommunityId(neighbourContextValue.communityData?.id);
          clearContext();
        }
      } else {
        setCurrentCommunityId("");
        clearContext();
      }
    }
  }, [neighbourContextValue.loading, neighbourContextValue.communityData]);

  React.useEffect(() => {
    setLoading(
      neighbourContextValue.loading ||
        loadingAnnounces ||
        loadingDeliveries ||
        loadingMeetings ||
        loadingTickets
    );
  }, [
    neighbourContextValue.loading,
    loadingAnnounces,
    loadingDeliveries,
    loadingMeetings,
    loadingTickets,
  ]);

  const dataContextValue = React.useMemo(
    () => ({
      announcesData,
      meetingsData,
      deliveriesData,
      ticketsData,
      loadMore,
      unsetAllLoaded,
      allAnnouncesLoaded,
      allMeetingsLoaded,
      allDeliveriesLoaded,
      allTicketsLoaded,
      loading,
    }),
    [
      announcesData,
      meetingsData,
      deliveriesData,
      ticketsData,
      loadMore,
      unsetAllLoaded,
      allAnnouncesLoaded,
      allMeetingsLoaded,
      allDeliveriesLoaded,
      allTicketsLoaded,
      loading,
    ]
  );

  return (
    <DataContext.Provider value={dataContextValue}>
      {children}
    </DataContext.Provider>
  );
};

export default DataContextComponent;
